import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui";
import { motPluriel, pluriel } from "@repo/utils";
import { Loader2, Mail } from "lucide-react";
import type { Id } from "../../../convex/_generated/dataModel";

type ParcoursStat = {
  _id: Id<"parcoursEvaluations">;
  prenomCandidat?: string;
  nomCandidat?: string;
  emailCandidat?: string;
  lienEnvoye?: boolean;
};

interface EnvoyerLiensDialogProps {
  isOpen: boolean;
  onClose: () => void;
  parcours: ParcoursStat[];
  selectedIds: Id<"parcoursEvaluations">[];
  sendingEmails: boolean;
  onToggle: (id: Id<"parcoursEvaluations">) => void;
  onToggleAll: () => void;
  onSend: () => void;
  /** Échec du dernier envoi, affiché ici : c'est d'ici que l'action part. */
  erreur?: string | null;
}

export function EnvoyerLiensDialog({
  isOpen,
  onClose,
  parcours,
  selectedIds,
  sendingEmails,
  onToggle,
  onToggleAll,
  onSend,
  erreur,
}: EnvoyerLiensDialogProps) {
  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Envoyer les liens par email"
      className="max-w-2xl"
    >
      <div className="space-y-4">
        {parcours.length > 0 && (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {pluriel(selectedIds.length, "candidat")}{" "}
                {motPluriel(selectedIds.length, "sélectionné")} sur {parcours.length}
              </p>
              <Button variant="ghost" size="sm" onClick={onToggleAll}>
                {selectedIds.length === parcours.length
                  ? "Tout désélectionner"
                  : "Tout sélectionner"}
              </Button>
            </div>

            <div className="max-h-[400px] overflow-y-auto border rounded-surface">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]"></TableHead>
                    <TableHead>Candidat</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="w-[100px]">Lien</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parcours.map((p) => (
                    <TableRow
                      key={p._id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => onToggle(p._id)}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.includes(p._id)}
                          onCheckedChange={() => onToggle(p._id)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {p.prenomCandidat} {p.nomCandidat}
                      </TableCell>
                      <TableCell>{p.emailCandidat}</TableCell>
                      <TableCell>
                        {p.lienEnvoye ? (
                          <Badge variant="success" className="text-xs">
                            Envoyé
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="text-xs text-muted-foreground"
                          >
                            Non envoyé
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {erreur && (
              <p
                role="alert"
                className="rounded-surface border border-etat-erreur-trait bg-etat-erreur-surface px-4 py-3 text-sm text-etat-erreur-contenu"
              >
                L'envoi a échoué : {erreur}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Annuler
              </Button>
              <Button
                disabled={selectedIds.length === 0 || sendingEmails}
                onClick={onSend}
              >
                {sendingEmails ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Envoyer ({selectedIds.length})
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
